import React, { useState, useEffect } from "react";
import {
  useParams,
  useNavigate,
  useSearchParams,
  Link,
} from "react-router-dom";
import { useUser } from "@clerk/clerk-react";
import { useBuilding } from "../../context/BuildingContext";
import PageHeader from "../../components/common/PageHeader";
import Spinner from "../../components/common/Spinner";
import ErrorAlert from "../../components/common/ErrorAlert";
import ConfirmDialog from "../../components/common/ConfirmDialog";
import { financialRecordsApi, bankTransactionsApi } from "../../services/api";
import { formatCurrency, formatDate } from "../../utils/formatters";
import type {
  FinancialRecord,
  FinancialRecordType,
  FinancialRecordStatus,
  BankTransaction,
} from "@akfdev-com/shared/src/models";
import FinancialRecordCreateForm from "./FinancialRecordCreateForm";

const TYPE_BADGE: Record<FinancialRecordType, string> = {
  invoice: "bg-blue-100 text-blue-700",
  bill: "bg-purple-100 text-purple-700",
  payment: "bg-teal-100 text-teal-700",
  service_charge: "bg-orange-100 text-orange-700",
};

const STATUS_BADGE: Record<FinancialRecordStatus, string> = {
  pending: "bg-yellow-100 text-yellow-700",
  approved: "bg-blue-100 text-blue-700",
  rejected: "bg-red-100 text-red-700",
  paid: "bg-green-100 text-green-700",
  partial: "bg-blue-100 text-blue-700",
  completed: "bg-green-100 text-green-700",
  failed: "bg-red-100 text-red-700",
};

const FinancialRecordDetails: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { user } = useUser();
  const { currentBuilding } = useBuilding();
  const buildingId =
    searchParams.get("buildingId") || currentBuilding?.id || "";

  // Record state
  const [record, setRecord] = useState<FinancialRecord | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const [actionError, setActionError] = useState<string | null>(null);

  // Confirm dialogs
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);

  // Approval
  const [showRejectInput, setShowRejectInput] = useState(false);
  const [rejectionReason, setRejectionReason] = useState("");

  // Reconcile modal state
  const [showReconcileModal, setShowReconcileModal] = useState(false);
  const [bankTransactions, setBankTransactions] = useState<BankTransaction[]>(
    [],
  );
  const [txSearch, setTxSearch] = useState("");
  const [txLoading, setTxLoading] = useState(false);
  const [selectedTxId, setSelectedTxId] = useState<string>("");

  // Cross-reference: when the record is reconciled, lazy-fetch the linked
  // bank transaction so the "Bank Reconciliation" panel can render a
  // clickable "View transaction" link that navigates directly to the
  // tx's edit page (crossref-bug Slice 2c). The FR only stores the tx id;
  // the bank account id needed to construct the URL comes from the tx.
  const [linkedTx, setLinkedTx] = useState<BankTransaction | null>(null);

  useEffect(() => {
    const fetchRecord = async () => {
      if (!id) {
        // Creating a new record, not loading an existing one
        setLoading(false);
        return;
      }
      try {
        setLoading(true);
        const data = await financialRecordsApi.get(id, buildingId || undefined);
        setRecord(data);
      } catch {
        setError("Failed to load financial record.");
      } finally {
        setLoading(false);
      }
    };
    fetchRecord();
  }, [id, buildingId]);

  // Lazy-fetch the linked bank transaction so the "Bank Reconciliation"
  // panel can render a clickable "View transaction" link. Fires only when
  // the record has a `bankTransactionId` — otherwise the panel just shows
  // "Not reconciled" and there's nothing to fetch. Failure is silent
  // (fallback to plain-text tx id) so a temporary bank-tx API blip
  // doesn't break the whole FR detail page.
  useEffect(() => {
    const fetchLinkedTx = async () => {
      if (!record?.bankTransactionId || !record?.buildingId) {
        setLinkedTx(null);
        return;
      }
      try {
        const tx = await bankTransactionsApi.get(
          record.bankTransactionId,
          record.buildingId,
        );
        setLinkedTx(tx);
      } catch {
        setLinkedTx(null);
      }
    };
    fetchLinkedTx();
  }, [record?.bankTransactionId, record?.buildingId]);

  // ── Actions ──────────────────────────────────────────────────────────────

  const doStatusUpdate = async (
    status: FinancialRecordStatus,
    extra?: Partial<FinancialRecord>,
  ) => {
    if (!record) return;
    try {
      setSaving(true);
      setActionError(null);
      const updated = await financialRecordsApi.update(id!, {
        buildingId: record.buildingId,
        status,
        ...extra,
      });
      setRecord(updated.record ?? updated);
    } catch (err: any) {
      setActionError(err?.response?.data?.message || "Action failed.");
    } finally {
      setSaving(false);
    }
  };

  const handleApprove = () =>
    doStatusUpdate("approved", {
      approvedBy: user?.fullName || user?.primaryEmailAddress?.emailAddress,
      approvedDate: new Date().toISOString().split("T")[0],
    });

  const handleReject = () => {
    if (!rejectionReason.trim()) return;
    doStatusUpdate("rejected", { rejectionReason });
    setShowRejectInput(false);
    setRejectionReason("");
  };

  const handleMarkPaid = () => doStatusUpdate("paid");

  const handleMarkComplete = () => doStatusUpdate("completed");

  const handleConvertType = async (newType: FinancialRecordType) => {
    if (!record) return;
    try {
      setSaving(true);
      setActionError(null);
      const updated = await financialRecordsApi.update(id!, {
        buildingId: record.buildingId,
        type: newType,
      });
      setRecord(updated.record ?? updated);
    } catch (err: any) {
      setActionError(err?.response?.data?.message || "Could not convert type.");
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!record) return;
    try {
      await financialRecordsApi.delete(id!, record.buildingId);
      navigate("/dashboard/financial-records");
    } catch (err: any) {
      setActionError("Failed to delete record.");
    } finally {
      setShowDeleteConfirm(false);
    }
  };

  // ── Reconciliation ────────────────────────────────────────────────────────

  const openReconcileModal = async () => {
    if (!record) return;
    setShowReconcileModal(true);
    setTxLoading(true);
    try {
      const txs = await bankTransactionsApi.getAll(record.buildingId);
      setBankTransactions(Array.isArray(txs) ? txs : []);
    } catch {
      setBankTransactions([]);
    } finally {
      setTxLoading(false);
    }
  };

  const handleReconcile = async () => {
    if (!record || !selectedTxId) return;
    try {
      setSaving(true);
      setActionError(null);
      await financialRecordsApi.reconcile(id!, record.buildingId, selectedTxId);
      const updated = await financialRecordsApi.get(id!, record.buildingId);
      setRecord(updated);
      setShowReconcileModal(false);
      setSelectedTxId("");
    } catch (err: any) {
      setActionError(err?.response?.data?.message || "Reconciliation failed.");
    } finally {
      setSaving(false);
    }
  };

  const filteredTxs = bankTransactions.filter((tx) => {
    if (!txSearch) return true;
    const s = txSearch.toLowerCase();
    return (
      tx.description?.toLowerCase().includes(s) ||
      tx.reference?.toLowerCase().includes(s) ||
      tx.date?.includes(s)
    );
  });

  // ── Render ────────────────────────────────────────────────────────────────

  if (loading) return <Spinner message="Loading record..." />;
  if (error) return <ErrorAlert message={error} />;

  // For create flow (no id), show form with empty record
  const isCreateMode = !id;
  if (isCreateMode) {
    return <FinancialRecordCreateForm />;
  }

  if (!record) return <ErrorAlert message="Record not found." />;

  const isInvoice = record.type === "invoice";
  const isBill = record.type === "bill";
  const isPayment = record.type === "payment";
  const isReconciled = !!record.bankTransactionId;

  return (
    <div>
      <PageHeader
        title={
          isInvoice
            ? record.invoiceNumber || "Invoice"
            : isBill
            ? record.title || "Bill"
            : record.paymentNumber || "Service Charge"
        }
        description={record.description}
        actions={
          <div className="flex gap-2 flex-wrap">
            {/* Type badge */}
            <span
              className={`self-center inline-flex items-center px-2.5 py-1 rounded-full text-xs font-semibold ${
                TYPE_BADGE[record.type]
              }`}
            >
              {record.type}
            </span>

            {/* Status badge */}
            <span
              className={`self-center inline-flex items-center px-2.5 py-1 rounded-full text-xs font-semibold ${
                STATUS_BADGE[record.status] ?? "bg-gray-100 text-gray-600"
              }`}
            >
              {record.status}
            </span>

            {/* Approve */}
            {record.status === "pending" && !isPayment && (
              <button
                onClick={handleApprove}
                disabled={saving}
                className="px-3 py-1.5 text-sm font-medium rounded-md text-white bg-green-600 hover:bg-green-700 disabled:opacity-50"
              >
                Approve
              </button>
            )}

            {/* Reject */}
            {(record.status === "pending" || record.status === "approved") &&
              !isPayment && (
                <button
                  onClick={() => setShowRejectInput(!showRejectInput)}
                  disabled={saving}
                  className="px-3 py-1.5 text-sm font-medium rounded-md border border-red-300 text-red-700 bg-white hover:bg-red-50 disabled:opacity-50"
                >
                  Reject
                </button>
              )}

            {/* Mark Paid */}
            {record.status === "approved" && !isPayment && (
              <button
                onClick={handleMarkPaid}
                disabled={saving}
                className="px-3 py-1.5 text-sm font-medium rounded-md text-white bg-primary-600 hover:bg-primary-700 disabled:opacity-50"
              >
                Mark Paid
              </button>
            )}

            {/* Mark Complete (payment type) */}
            {isPayment && record.status === "pending" && (
              <button
                onClick={handleMarkComplete}
                disabled={saving}
                className="px-3 py-1.5 text-sm font-medium rounded-md text-white bg-primary-600 hover:bg-primary-700 disabled:opacity-50"
              >
                Mark Completed
              </button>
            )}

            {/* Convert type (invoice <-> bill only) */}
            {isInvoice && (
              <button
                onClick={() => handleConvertType("bill")}
                disabled={saving}
                className="px-3 py-1.5 text-sm font-medium rounded-md border border-purple-300 text-purple-700 bg-white hover:bg-purple-50 disabled:opacity-50"
              >
                Convert to Bill
              </button>
            )}
            {isBill && (
              <button
                onClick={() => handleConvertType("invoice")}
                disabled={saving}
                className="px-3 py-1.5 text-sm font-medium rounded-md border border-blue-300 text-blue-700 bg-white hover:bg-blue-50 disabled:opacity-50"
              >
                Convert to Invoice
              </button>
            )}

            {/* Reconcile */}
            {!isReconciled && (
              <button
                onClick={openReconcileModal}
                disabled={saving}
                className="px-3 py-1.5 text-sm font-medium rounded-md border border-gray-300 text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50"
              >
                Reconcile
              </button>
            )}

            {/* Delete */}
            <button
              onClick={() => setShowDeleteConfirm(true)}
              disabled={saving}
              className="px-3 py-1.5 text-sm font-medium rounded-md border border-red-200 text-red-600 bg-white hover:bg-red-50 disabled:opacity-50"
            >
              Delete
            </button>

            <Link
              to="/dashboard/financial-records"
              className="px-3 py-1.5 text-sm font-medium rounded-md border border-gray-300 text-gray-700 bg-white hover:bg-gray-50"
            >
              ← Back
            </Link>
          </div>
        }
      />

      {actionError && <ErrorAlert message={actionError} />}

      {/* Reject input */}
      {showRejectInput && (
        <div className="mt-4 bg-red-50 border border-red-200 rounded-lg p-4">
          <label className="block text-sm font-medium text-red-800 mb-1">
            Rejection reason
          </label>
          <input
            type="text"
            value={rejectionReason}
            onChange={(e) => setRejectionReason(e.target.value)}
            className="block w-full text-sm border border-red-300 rounded-md px-3 py-2"
            placeholder="Enter reason..."
          />
          <div className="mt-2 flex gap-2">
            <button
              onClick={handleReject}
              disabled={!rejectionReason.trim()}
              className="px-3 py-1.5 text-sm font-medium rounded-md text-white bg-red-600 hover:bg-red-700 disabled:opacity-50"
            >
              Confirm Reject
            </button>
            <button
              onClick={() => {
                setShowRejectInput(false);
                setRejectionReason("");
              }}
              className="px-3 py-1.5 text-sm font-medium rounded-md border border-gray-300 text-gray-700 bg-white"
            >
              Cancel
            </button>
          </div>
        </div>
      )}

      {/* Details card */}
      <div className="mt-6 bg-white shadow overflow-hidden sm:rounded-lg">
        <div className="border-t border-gray-200">
          <dl>
            <div className="bg-gray-50 px-4 py-4 sm:grid sm:grid-cols-3 sm:gap-4 sm:px-6">
              <dt className="text-sm font-medium text-gray-500">Building</dt>
              <dd className="mt-1 text-sm text-gray-900 sm:mt-0 sm:col-span-2">
                {currentBuilding?.name}
              </dd>
            </div>
            <div className="bg-white px-4 py-4 sm:grid sm:grid-cols-3 sm:gap-4 sm:px-6">
              <dt className="text-sm font-medium text-gray-500">Date</dt>
              <dd className="mt-1 text-sm text-gray-900 sm:mt-0 sm:col-span-2">
                {formatDate(record.date)}
              </dd>
            </div>
            {record.dueDate && (
              <div className="bg-gray-50 px-4 py-4 sm:grid sm:grid-cols-3 sm:gap-4 sm:px-6">
                <dt className="text-sm font-medium text-gray-500">Due Date</dt>
                <dd className="mt-1 text-sm text-gray-900 sm:mt-0 sm:col-span-2">
                  {formatDate(record.dueDate)}
                </dd>
              </div>
            )}
            <div className="bg-white px-4 py-4 sm:grid sm:grid-cols-3 sm:gap-4 sm:px-6">
              <dt className="text-sm font-medium text-gray-500">Amount</dt>
              <dd className="mt-1 text-sm font-semibold text-gray-900 sm:mt-0 sm:col-span-2">
                {formatCurrency(record.amount / 100)}
              </dd>
            </div>
            {record.category && (
              <div className="bg-gray-50 px-4 py-4 sm:grid sm:grid-cols-3 sm:gap-4 sm:px-6">
                <dt className="text-sm font-medium text-gray-500">Category</dt>
                <dd className="mt-1 text-sm text-gray-900 sm:mt-0 sm:col-span-2">
                  {record.category}
                  {record.nominalCode ? ` (${record.nominalCode})` : ""}
                </dd>
              </div>
            )}
            {record.reference && (
              <div className="bg-white px-4 py-4 sm:grid sm:grid-cols-3 sm:gap-4 sm:px-6">
                <dt className="text-sm font-medium text-gray-500">Reference</dt>
                <dd className="mt-1 text-sm text-gray-900 sm:mt-0 sm:col-span-2">
                  {record.reference}
                </dd>
              </div>
            )}

            {/* ── Invoice-specific ─────────────────────────────────────── */}
            {isInvoice && (
              <>
                {record.supplierName && (
                  <div className="bg-gray-50 px-4 py-4 sm:grid sm:grid-cols-3 sm:gap-4 sm:px-6">
                    <dt className="text-sm font-medium text-gray-500">
                      Supplier
                    </dt>
                    <dd className="mt-1 text-sm text-gray-900 sm:mt-0 sm:col-span-2">
                      {record.supplierName}
                      {record.supplierAddress && (
                        <div className="text-gray-500 text-xs mt-0.5">
                          {record.supplierAddress}
                        </div>
                      )}
                    </dd>
                  </div>
                )}
                {record.invoiceNumber && (
                  <div className="bg-white px-4 py-4 sm:grid sm:grid-cols-3 sm:gap-4 sm:px-6">
                    <dt className="text-sm font-medium text-gray-500">
                      Invoice Number
                    </dt>
                    <dd className="mt-1 text-sm text-gray-900 sm:mt-0 sm:col-span-2">
                      {record.invoiceNumber}
                    </dd>
                  </div>
                )}
                {record.items && record.items.length > 0 && (
                  <div className="bg-gray-50 px-4 py-4 sm:px-6">
                    <dt className="text-sm font-medium text-gray-500 mb-3">
                      Line Items
                    </dt>
                    <table className="min-w-full text-sm divide-y divide-gray-100">
                      <thead>
                        <tr className="text-xs text-gray-400 uppercase">
                          <th className="text-left pb-2">Description</th>
                          <th className="text-right pb-2">Qty</th>
                          <th className="text-right pb-2">Unit Price</th>
                          <th className="text-right pb-2">Total</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-gray-100">
                        {record.items.map((item) => (
                          <tr key={item.id}>
                            <td className="py-1.5 text-gray-900">
                              {item.description}
                            </td>
                            <td className="py-1.5 text-right text-gray-600">
                              {item.quantity}
                            </td>
                            <td className="py-1.5 text-right text-gray-600">
                              {formatCurrency(item.unitPrice)}
                            </td>
                            <td className="py-1.5 text-right font-medium text-gray-900">
                              {formatCurrency(item.totalPrice)}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </>
            )}

            {/* ── Bill-specific ────────────────────────────────────────── */}
            {isBill && (
              <>
                {record.submittedBy && (
                  <div className="bg-gray-50 px-4 py-4 sm:grid sm:grid-cols-3 sm:gap-4 sm:px-6">
                    <dt className="text-sm font-medium text-gray-500">
                      Submitted By
                    </dt>
                    <dd className="mt-1 text-sm text-gray-900 sm:mt-0 sm:col-span-2">
                      {record.submittedBy}
                    </dd>
                  </div>
                )}
                {record.approvedBy && (
                  <div className="bg-white px-4 py-4 sm:grid sm:grid-cols-3 sm:gap-4 sm:px-6">
                    <dt className="text-sm font-medium text-gray-500">
                      Approved By
                    </dt>
                    <dd className="mt-1 text-sm text-gray-900 sm:mt-0 sm:col-span-2">
                      {record.approvedBy}
                      {record.approvedDate
                        ? ` on ${formatDate(record.approvedDate)}`
                        : ""}
                    </dd>
                  </div>
                )}
                {record.rejectionReason && (
                  <div className="bg-red-50 px-4 py-4 sm:grid sm:grid-cols-3 sm:gap-4 sm:px-6">
                    <dt className="text-sm font-medium text-red-600">
                      Rejection Reason
                    </dt>
                    <dd className="mt-1 text-sm text-red-700 sm:mt-0 sm:col-span-2">
                      {record.rejectionReason}
                    </dd>
                  </div>
                )}
                {record.receiptUrl && (
                  <div className="bg-gray-50 px-4 py-4 sm:grid sm:grid-cols-3 sm:gap-4 sm:px-6">
                    <dt className="text-sm font-medium text-gray-500">
                      Receipt
                    </dt>
                    <dd className="mt-1 text-sm sm:mt-0 sm:col-span-2">
                      <a
                        href={record.receiptUrl}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-primary-600 hover:underline"
                      >
                        View Receipt
                      </a>
                    </dd>
                  </div>
                )}
              </>
            )}

            {/* ── Payment-specific ─────────────────────────────────────── */}
            {isPayment && (
              <>
                {record.unitName && (
                  <div className="bg-gray-50 px-4 py-4 sm:grid sm:grid-cols-3 sm:gap-4 sm:px-6">
                    <dt className="text-sm font-medium text-gray-500">Unit</dt>
                    <dd className="mt-1 text-sm text-gray-900 sm:mt-0 sm:col-span-2">
                      {record.unitName}
                    </dd>
                  </div>
                )}
                {record.serviceChargePlanId && (
                  <div className="bg-white px-4 py-4 sm:grid sm:grid-cols-3 sm:gap-4 sm:px-6">
                    <dt className="text-sm font-medium text-gray-500">
                      Service Charge Plan
                    </dt>
                    <dd className="mt-1 text-sm text-gray-900 sm:mt-0 sm:col-span-2">
                      {record.serviceChargePlanId}
                    </dd>
                  </div>
                )}
              </>
            )}

            {/* ── Reconciliation link ──────────────────────────────────── */}
            <div
              className={`${
                isPayment ? "bg-gray-50" : "bg-white"
              } px-4 py-4 sm:grid sm:grid-cols-3 sm:gap-4 sm:px-6`}
            >
              <dt className="text-sm font-medium text-gray-500">
                Bank Reconciliation
              </dt>
              <dd className="mt-1 text-sm sm:mt-0 sm:col-span-2">
                {isReconciled ? (
                  <div className="space-y-1">
                    <span className="inline-flex items-center gap-2">
                      <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-green-100 text-green-700">
                        Reconciled
                      </span>
                      {/* Cross-reference link (crossref-bug Slice 2c).
                          When the linked tx has loaded, render a
                          clickable link to its account+edit page. Falls
                          back to plain-text id when the tx fetch failed
                          or is still in flight. */}
                      {linkedTx ? (
                        <Link
                          to={`/dashboard/banking/${linkedTx.bankAccountId}/transactions/${linkedTx.id}/edit?buildingId=${record.buildingId}`}
                          className="text-xs text-primary-600 hover:underline"
                        >
                          View transaction →
                        </Link>
                      ) : (
                        <span className="text-gray-500 text-xs">
                          Tx: {record.bankTransactionId}
                        </span>
                      )}
                    </span>
                    {linkedTx && (
                      <div className="text-xs text-gray-500">
                        {linkedTx.bankAccountName} · {linkedTx.date} ·{" "}
                        {formatCurrency(linkedTx.grossValue / 100)}
                        {linkedTx.reference ? ` · ${linkedTx.reference}` : ""}
                      </div>
                    )}
                  </div>
                ) : (
                  <span className="text-gray-400 text-xs">Not reconciled</span>
                )}
              </dd>
            </div>

            {/* ── Journal entry link ───────────────────────────────────── */}
            {record.journalEntryId && (
              <div className="bg-gray-50 px-4 py-4 sm:grid sm:grid-cols-3 sm:gap-4 sm:px-6">
                <dt className="text-sm font-medium text-gray-500">
                  Journal Entry
                </dt>
                <dd className="mt-1 text-sm sm:mt-0 sm:col-span-2">
                  <Link
                    to={`/dashboard/journal/${record.journalEntryId}`}
                    className="text-primary-600 hover:underline"
                  >
                    View Journal Entry →
                  </Link>
                </dd>
              </div>
            )}

            {/* Audit */}
            <div className="bg-white px-4 py-4 sm:grid sm:grid-cols-3 sm:gap-4 sm:px-6">
              <dt className="text-sm font-medium text-gray-500">Created</dt>
              <dd className="mt-1 text-sm text-gray-500 sm:mt-0 sm:col-span-2">
                {formatDate(record.createdAt)}
                {record.createdBy ? ` by ${record.createdBy}` : ""}
              </dd>
            </div>
            {record.updatedAt && (
              <div className="bg-gray-50 px-4 py-4 sm:grid sm:grid-cols-3 sm:gap-4 sm:px-6">
                <dt className="text-sm font-medium text-gray-500">
                  Last Updated
                </dt>
                <dd className="mt-1 text-sm text-gray-500 sm:mt-0 sm:col-span-2">
                  {formatDate(record.updatedAt)}
                </dd>
              </div>
            )}
          </dl>
        </div>
      </div>

      {/* Reconcile Modal */}
      {showReconcileModal && (
        <div className="fixed inset-0 z-50 flex items-start justify-center bg-gray-900 bg-opacity-50 pt-16 px-4">
          <div className="bg-white rounded-lg shadow-xl w-full max-w-2xl">
            <div className="px-5 py-4 border-b border-gray-200 flex items-center justify-between">
              <h2 className="text-base font-semibold text-gray-900">
                Link Bank Transaction
              </h2>
              <button
                onClick={() => setShowReconcileModal(false)}
                className="text-gray-400 hover:text-gray-600 text-xl leading-none"
              >
                &times;
              </button>
            </div>
            <div className="px-5 py-4">
              <p className="text-sm text-gray-500 mb-3">
                Select a bank transaction to link with this {record.type}. Both
                records will be updated atomically.
              </p>
              <input
                type="text"
                placeholder="Search transactions..."
                value={txSearch}
                onChange={(e) => setTxSearch(e.target.value)}
                className="block w-full text-sm border border-gray-300 rounded-md px-3 py-2 mb-3"
              />
              {txLoading ? (
                <Spinner message="Loading transactions..." />
              ) : filteredTxs.length === 0 ? (
                <p className="text-sm text-gray-400 text-center py-4">
                  No transactions found
                </p>
              ) : (
                <div className="border border-gray-200 rounded overflow-auto max-h-64">
                  <table className="min-w-full text-sm">
                    <thead className="bg-gray-50">
                      <tr>
                        <th className="px-3 py-2 text-left text-xs text-gray-500">
                          Select
                        </th>
                        <th className="px-3 py-2 text-left text-xs text-gray-500">
                          Date
                        </th>
                        <th className="px-3 py-2 text-left text-xs text-gray-500">
                          Description
                        </th>
                        <th className="px-3 py-2 text-right text-xs text-gray-500">
                          Amount
                        </th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-100">
                      {filteredTxs.map((tx) => (
                        <tr
                          key={tx.id}
                          className={`cursor-pointer hover:bg-primary-50 ${
                            selectedTxId === tx.id ? "bg-primary-50" : ""
                          }`}
                          onClick={() => setSelectedTxId(tx.id)}
                        >
                          <td className="px-3 py-2">
                            <input
                              type="radio"
                              readOnly
                              checked={selectedTxId === tx.id}
                              className="accent-primary-600"
                            />
                          </td>
                          <td className="px-3 py-2 text-gray-500 whitespace-nowrap">
                            {formatDate(tx.date)}
                          </td>
                          <td className="px-3 py-2 text-gray-900">
                            {tx.description}
                          </td>
                          <td
                            className={`px-3 py-2 text-right font-mono ${
                              tx.grossValue >= 0
                                ? "text-green-700"
                                : "text-red-600"
                            }`}
                          >
                            {formatCurrency(tx.grossValue / 100)}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
            <div className="px-5 py-3 border-t border-gray-200 flex justify-end gap-2">
              <button
                onClick={() => setShowReconcileModal(false)}
                className="px-3 py-1.5 text-sm font-medium rounded-md border border-gray-300 text-gray-700 bg-white hover:bg-gray-50"
              >
                Cancel
              </button>
              <button
                onClick={handleReconcile}
                disabled={!selectedTxId || saving}
                className="px-3 py-1.5 text-sm font-medium rounded-md text-white bg-primary-600 hover:bg-primary-700 disabled:opacity-50"
              >
                {saving ? "Linking..." : "Link Transaction"}
              </button>
            </div>
          </div>
        </div>
      )}

      <ConfirmDialog
        isOpen={showDeleteConfirm}
        title="Delete Financial Record"
        message="This will permanently delete the record. Are you sure?"
        confirmText="Delete"
        onConfirm={handleDelete}
        onClose={() => setShowDeleteConfirm(false)}
      />
    </div>
  );
};

export default FinancialRecordDetails;
